import React, { useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faEye, faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { translate } from '../../../services/translate';
import MyAppText from '../../MyAppText';
import MyDataTable from '../../MyDataTable';
import InvolvedVehicleModal from './InvolvedVehicleModal';
import CustomButton from '../../CustomButton';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import AlertModal from '../../AlertModal';
import { TranslateRich } from '../../TranslateRich';

interface InvolvedVehiclesParams {
    involvedVehicles: InvolvedVehicle[];
    setInvolvedVehicles: (value: InvolvedVehicle[]) => void;
    actingBodyId: number;
    occurrenceSituation: OccurrenceSituation;
}

export default function InvolvedVehicles({ involvedVehicles, setInvolvedVehicles, actingBodyId, occurrenceSituation }: InvolvedVehiclesParams) {
    const { styles, theme } = useStyles(styleSheet);
    const [deleteVehicleModalVisible, setDeleteVehicleModalVisible] = useState(false);
    const [vehicleModalOpen, setVehicleModalOpen] = useState<boolean>(false);
    const [selectedInvolvedVehicle, setSelectedInvolvedVehicle] = useState<InvolvedVehicle>(getBlankVehicle());

    function getBlankVehicle(): InvolvedVehicle {
        return {
            id: undefined,
            brand: '',
            chassis: '',
            color: '',
            model: '',
            plate: '',
            vehicleRelationId: 0,
            stolen: false,
            restriction: ''
        };
    }

    async function createUpdateInvolvedVehicles() {
        if (!selectedInvolvedVehicle.id) {
            selectedInvolvedVehicle.id = -Math.random();
            involvedVehicles.push(selectedInvolvedVehicle);
        } else {
            const index = involvedVehicles.findIndex(vehicle => vehicle.id === selectedInvolvedVehicle.id);
            involvedVehicles[index] = { ...involvedVehicles[index], ...selectedInvolvedVehicle };
        }

        setInvolvedVehicles(involvedVehicles);
        setSelectedInvolvedVehicle(getBlankVehicle());
    }

    return (
        <>
            <View style={styles.container}>
                {occurrenceSituation == 'concluded' ?
                    <></> :
                    <View style={styles.header}>
                        <MyAppText style={styles.headerText}>{translate('involvedVehicles')}</MyAppText>
                        <CustomButton
                            onPress={() => setVehicleModalOpen(true)}
                            text={translate('attach')}
                            icon={faPlus}
                        />
                    </View>
                }
                <MyDataTable
                    columns={[
                        {
                            name: translate('plate'),
                            selector: row => row.plate,
                            wrap: true,
                        },
                        {
                            name: translate('brand'),
                            selector: row => row.brand,
                            wrap: true,
                        },
                        {
                            name: translate('model'),
                            selector: row => row.model,
                            wrap: true,
                        },
                        {
                            name: translate('color'),
                            selector: row => row.color,
                            wrap: true,
                        },
                        {
                            name: translate('chassis'),
                            selector: row => row.chassis,
                            wrap: true,
                        },
                        {
                            name: translate('relation'),
                            selector: row => row.vehicleRelation?.name || '',
                            wrap: true,
                        },
                        {
                            name: translate('action'),
                            button: true,
                            cell: row =>
                                <View style={{ flexDirection: 'row', columnGap: 10 }}>
                                    <TouchableOpacity onPress={() => {
                                        setSelectedInvolvedVehicle(row);
                                        setVehicleModalOpen(true);
                                    }}>
                                        <FontAwesomeIcon icon={occurrenceSituation == 'concluded' ? faEye : faEdit} fontSize={16} color={theme.colors.iconColor} />
                                    </TouchableOpacity>
                                    {occurrenceSituation == 'concluded' ? <></> :
                                        <TouchableOpacity onPress={() => {
                                            setSelectedInvolvedVehicle(row);
                                            setDeleteVehicleModalVisible(true);
                                        }}>
                                            <FontAwesomeIcon icon={faTrashCan} fontSize={16} color={theme.colors.iconColor} />
                                        </TouchableOpacity>}
                                </View>
                        }
                    ]}
                    data={involvedVehicles}
                />
            </View>
            <AlertModal
                isModalVisible={deleteVehicleModalVisible}
                setModalVisible={setDeleteVehicleModalVisible}
                description={<>
                    <TranslateRich
                        i18nKey={'deleteInvolvedVehicleDescription'}
                        values={{
                            plate: <strong>{selectedInvolvedVehicle.plate || ''}</strong>,
                        }}
                    />
                    <br />
                    <br />
                    <TranslateRich i18nKey={'proceedWithDeletion'} />
                </>}
                title={translate('deleteInvolvedVehicle')}
                onSubmit={() => {
                    if (!selectedInvolvedVehicle) {
                        return;
                    }

                    const index = involvedVehicles.findIndex(vehicle => vehicle.id === selectedInvolvedVehicle.id);
                    if (index >= 0) {
                        involvedVehicles.splice(index, 1);
                        setInvolvedVehicles(involvedVehicles);
                        setSelectedInvolvedVehicle(getBlankVehicle());
                    }
                }}
                customButtonLabel={translate('yesDelete')}
                type='destructive'
            />
            <InvolvedVehicleModal
                involvedVehicle={selectedInvolvedVehicle}
                setInvolvedVehicle={setSelectedInvolvedVehicle}
                isModalVisible={vehicleModalOpen}
                actingBodyId={actingBodyId}
                setModalVisible={(value) => {
                    if (!value) {
                        setSelectedInvolvedVehicle(getBlankVehicle());
                    }
                    setVehicleModalOpen(value);
                }}
                onSubmit={createUpdateInvolvedVehicles}
                occurrenceSituation={occurrenceSituation}
            />
        </>
    );
}

const styleSheet = createStyleSheet(() => ({
    container: {
        flex: 1,
        rowGap: 20
    },
    headerText: {
        fontSize: 16,
        fontWeight: 'bold',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'space-between'
    }
}));
